import { useState, useEffect, useCallback, useRef } from "react"
import { ScrollContainer } from "react-indiana-drag-scroll"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import * as Yup from "yup"
import { useNavigate } from "react-router"
import { SlTooltip, SlIconButton, SlDrawer, SlInput, SlButton, SlIcon } from "@shoelace-style/shoelace/dist/react"
import api from "../../services/api"
import config from "../../config"
import { Toast } from "../../shared/Toast"
import NoDataFound from "../../shared/NoDataFound"
import Paginator from "../../shared/Paginator"
import TableLoader from "../../shared/TableLoader"
import { debounce } from "../../utils/Debounce"

const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/
const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/

const initialVendor = {
  vendor_name: "",
  email: "",
  mobile: "",
  password: "",
  address: "",
  pan: "",
  gst: "",
  accountno: "",
  ifsc: "",
  brand_url: "",
  status: true,
}

const buildSchema = (isEdit) =>
  Yup.object().shape({
    vendor_name: Yup.string().trim().required("Vendor Name is required"),
    email: Yup.string().trim().email("Invalid email").required("Email is required"),
    mobile: Yup.string()
      .matches(/^[6-9]\d{9}$/, "Invalid Mobile number")
      .required("Mobile Number is required"),
    password: isEdit
      ? Yup.string().notRequired()
      : Yup.string()
          .matches(/^[a-zA-Z0-9.\/!@#$%^&*+~?-]+$/, "Password should not contain any white space")
          .min(6, "Password must be at least 6 characters")
          .required("Password is required"),
    address: Yup.string().trim().required("Address is required"),
    pan: Yup.string()
      .transform((v) => (v ? v.toUpperCase() : v))
      .nullable()
      .notRequired()
      .test("pan", "Invalid PAN", (v) => !v || PAN_REGEX.test(v)),
    gst: Yup.string()
      .transform((v) => (v ? v.toUpperCase() : v))
      .nullable()
      .notRequired()
      .test("gst", "Invalid GST", (v) => !v || GST_REGEX.test(v)),
    accountno: Yup.string()
      .nullable()
      .notRequired()
      .test("accountno", "Invalid Account Number", (v) => !v || /^\d{9,18}$/.test(v)),
    ifsc: Yup.string()
      .transform((v) => (v ? v.toUpperCase() : v))
      .nullable()
      .notRequired()
      .test("ifsc", "Invalid IFSC", (v) => !v || IFSC_REGEX.test(v)),
    status: Yup.boolean(),
  })

function UserVendor() {
  const navigate = useNavigate()

  const [data, setData] = useState([])
  const [loader, setLoader] = useState(false)
  const [totalPages, setTotalPages] = useState(0)
  const [currentPage, setCurrentPage] = useState(0)
  const [search, setSearch] = useState("")

  const [show, setShow] = useState(false)
  const [flag, setFlag] = useState("add")
  const [editId, setEditId] = useState(null)

  const isEdit = flag === "edit"

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: initialVendor,
    resolver: yupResolver(buildSchema(isEdit)),
    context: { isEdit },
  })

  const brandUrlValue = watch("brand_url")
  const [uploadProgress, setUploadProgress] = useState(0)

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const accepted = ["image/jpg", "image/jpeg", "image/png", "image/webp"]
    if (!accepted.includes(file.type)) {
      Toast("error", "Only jpg, jpeg, png, webp formats accepted")
      e.target.value = null
      return
    }
    try {
      const formData = new FormData()
      formData.append("files", file)
      const res = await api({
        url: `/upload/brandImage`,
        method: "POST",
        responseType: "json",
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          setUploadProgress(Math.round((100 * evt.loaded) / evt.total))
        },
      })
      if (res.code === 200) {
        setValue("brand_url", res.data?.data || "", { shouldValidate: true })
        setUploadProgress(0)
      } else {
        Toast("error", res.data?.message || "Upload failed")
        setUploadProgress(0)
      }
    } catch (err) {
      Toast("error", err?.message || "Upload failed")
      setUploadProgress(0)
    }
  }

  const loadData = useCallback(
    async (searchString = "", page = 0) => {
      try {
        setLoader(true)
        const res = await api({
          url: `/vendor/list`,
          method: "POST",
          responseType: "json",
          data: {
            page,
            limit: config.PER_PAGE,
            search: searchString,
          },
        })
        setData(res?.data?.data?.length ? res.data.data : [])
        setTotalPages(Math.ceil((res?.data?.total || 0) / config.PER_PAGE))
      } catch (error) {
        if (error?.response?.status === 401) navigate("/")
        Toast("error", "Failed to load vendors")
      } finally {
        setLoader(false)
      }
    },
    [navigate],
  )

  useEffect(() => {
    loadData("", 0)
  }, [loadData])

  const handlePageClick = (event) => {
    setCurrentPage(event.selected)
    loadData(search, event.selected)
  }

  const debouncedSearchRef = useRef(
    debounce((value) => {
      setSearch(value)
      setCurrentPage(0)
      loadData(value, 0)
    }, 500),
  )

  const onSearchInput = (e) => {
    debouncedSearchRef.current(e.target.value)
  }

  const handleAdd = () => {
    setFlag("add")
    setEditId(null)
    reset(initialVendor)
    setShow(true)
  }

  const handleEdit = (item) => {
    setFlag("edit")
    setEditId(item.vendor_id)
    reset({
      vendor_name: item.vendor_name || "",
      email: item.email || "",
      mobile: item.mobile || "",
      password: "",
      address: item.address || "",
      pan: item.pan || "",
      gst: item.gst || "",
      accountno: item.accountno || "",
      ifsc: item.ifsc || "",
      brand_url: item.brand_url || "",
      status: item.status === true || item.status === 1,
    })
    setShow(true)
  }

  const handleClose = () => {
    setShow(false)
    setEditId(null)
    setFlag("add")
    reset(initialVendor)
  }

  const handleRequestClose = (event) => {
    if (event.detail.source === "overlay") event.preventDefault()
  }

  const onSubmit = async (values) => {
    try {
      const payload = { ...values }
      if (isEdit) delete payload.password

      const url = isEdit ? `/vendor/update/${editId}` : `/vendor/create`
      const method = isEdit ? "PUT" : "POST"
      const res = await api({ url, method, responseType: "json", data: payload })

      if (res.code !== 200) {
        Toast("error", res?.data?.message || res?.message || "Something went wrong")
        return
      }
      Toast("success", isEdit ? "Vendor updated successfully" : "Vendor created successfully")
      handleClose()
      setCurrentPage(0)
      loadData(search, 0)
    } catch (error) {
      if (error?.response?.status === 401) navigate("/")
      Toast("error", error?.response?.message || "Something went wrong")
    }
  }

  return (
    <>
      <div className="flex gap-4 justify-end my-4">
        <SlInput
          size="small"
          onSlInput={onSearchInput}
          placeholder="Search By Name / Email / Mobile.."
        >
          <SlIcon library="lucide" name="search" slot="prefix"></SlIcon>
        </SlInput>
        <SlButton size="small" variant="primary" onClick={handleAdd}>
          Add Vendor
        </SlButton>
      </div>

      <SlDrawer
        label={isEdit ? "Edit Vendor" : "Create Vendor"}
        open={show}
        onSlRequestClose={handleRequestClose}
        onSlAfterHide={handleClose}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-3">
            <label className="mb-1">Vendor Name</label>
            <span className="text-red-600">*</span>
            <input
              type="text"
              className="form-control"
              placeholder="Enter Vendor Name"
              {...register("vendor_name")}
            />
            {errors.vendor_name && <div className="text-red-600">{errors.vendor_name.message}</div>}
          </div>

          <div className="mb-3">
            <label className="mb-1">Email</label>
            <span className="text-red-600">*</span>
            <input
              type="email"
              className="form-control"
              placeholder="Enter Email"
              {...register("email")}
            />
            {errors.email && <div className="text-red-600">{errors.email.message}</div>}
          </div>

          <div className="mb-3">
            <label className="mb-1">Mobile No</label>
            <span className="text-red-600">*</span>
            <input
              type="tel"
              className="form-control"
              placeholder="Enter Mobile No"
              {...register("mobile")}
            />
            {errors.mobile && <div className="text-red-600">{errors.mobile.message}</div>}
          </div>

          {!isEdit && (
            <div className="mb-3">
              <label className="mb-1">Password</label>
              <span className="text-red-600">*</span>
              <input
                type="text"
                className="form-control"
                placeholder="Enter Password"
                {...register("password")}
              />
              {errors.password && <div className="text-red-600">{errors.password.message}</div>}
            </div>
          )}

          <div className="mb-3">
            <label className="mb-1">Address</label>
            <span className="text-red-600">*</span>
            <textarea
              className="form-control"
              placeholder="Enter Address"
              rows={2}
              {...register("address")}
            />
            {errors.address && <div className="text-red-600">{errors.address.message}</div>}
          </div>

          <div className="mb-3">
            <label className="mb-1">PAN</label>
            <input
              type="text"
              className="form-control"
              placeholder="ABCDE1234F"
              {...register("pan")}
            />
            {errors.pan && <div className="text-red-600">{errors.pan.message}</div>}
          </div>

          <div className="mb-3">
            <label className="mb-1">GST</label>
            <input
              type="text"
              className="form-control"
              placeholder="22ABCDE1234F1Z5"
              {...register("gst")}
            />
            {errors.gst && <div className="text-red-600">{errors.gst.message}</div>}
          </div>

          <div className="mb-3">
            <label className="mb-1">Account Number</label>
            <input
              type="text"
              className="form-control"
              placeholder="Enter Account Number"
              {...register("accountno")}
            />
            {errors.accountno && <div className="text-red-600">{errors.accountno.message}</div>}
          </div>

          <div className="mb-3">
            <label className="mb-1">IFSC Code</label>
            <input
              type="text"
              className="form-control"
              placeholder="Enter IFSC Code"
              {...register("ifsc")}
            />
            {errors.ifsc && <div className="text-red-600">{errors.ifsc.message}</div>}
          </div>

          <div className="mb-3">
            <label className="mb-1">Brand Image</label>
            <input
              type="file"
              className="form-control"
              accept="image/jpg,image/jpeg,image/png,image/webp"
              onChange={handleImageUpload}
            />
            {uploadProgress > 0 && uploadProgress < 100 && (
              <div className="progress mt-2" style={{ height: "5px" }}>
                <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
              </div>
            )}
            {brandUrlValue && (
              <div className="mt-2">
                <img
                  src={brandUrlValue}
                  alt="Brand"
                  style={{ width: "100px", height: "100px", objectFit: "cover", borderRadius: "5px" }}
                />
              </div>
            )}
            <input type="hidden" {...register("brand_url")} />
          </div>

          {isEdit && (
            <div className="mb-3 form-check form-switch">
              <label className="form-check-label">
                Status ({watch("status") ? "Active" : "Inactive"})
              </label>
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                checked={!!watch("status")}
                onChange={() => {
                  setValue("status", !watch("status"))
                }}
              />
            </div>
          )}

          <div slot="footer">
            <SlButton type="submit" className="w-full" variant="primary" size="small" disabled={isSubmitting}>
              {isEdit ? "Update" : "Submit"}
            </SlButton>
          </div>
        </form>
      </SlDrawer>

      <div className="mb-4">
        <ScrollContainer>
          <table className="table table-bordered">
            <thead className="sticky top-0">
              <tr>
                <th>Vendor Name</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            {data?.length && !loader ? (
              <tbody>
                {data.map((item) => (
                  <tr key={item.vendor_id} className="hover:bg-slate-100">
                    <td>{item.vendor_name}</td>
                    <td>{item.email}</td>
                    <td>{item.mobile}</td>
                    <td>
                      <span className={item.status === true || item.status === 1 ? "full-badge text-center" : "unpaid-badge text-center"}>
                        {item.status === true || item.status === 1 ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <SlTooltip content="Edit Vendor">
                        <SlIconButton
                          library="lucide"
                          name="edit"
                          onClick={() => handleEdit(item)}
                        ></SlIconButton>
                      </SlTooltip>
                    </td>
                  </tr>
                ))}
              </tbody>
            ) : !data?.length && !loader ? (
              <NoDataFound colspan="5" />
            ) : (
              <TableLoader colspan="5" />
            )}
          </table>
        </ScrollContainer>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-end">
          <Paginator pageCount={totalPages} handlePageClick={handlePageClick} forcePage={currentPage} />
        </div>
      )}
    </>
  )
}

export default UserVendor
